import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useParams, useLocation, useHistory } from 'react-router-dom';
import { Card } from '@curves/ui-base';
import { TabsCardLayout } from '@curves/ui-shared';
import useClubInventoryApi from '@Apis/useClubInventoryApi';
import { PageLayout } from '@Local/Components/Layout';
import useTabState from '@Util/hook/useTabState';
import useOptions from '@Util/hook/useOptions';
import { CLUB_INVENTORY_SHOP_STOCK_OVERVIEW_ROUTER } from '@Router/routerPath';
import { INVENTORY_RECORD_TYPE } from '@Constants/';
import { ShopStockSummaryCard, ShopStockChangeHistory, ShopStockDefectRecords } from './components';

const TAB_VALUES = {
    CHANGE_HISTORY: 'change-history',
    DEFECT_RECORDS: 'defect-records',
};

const TABS = [
    { label: '異動紀錄', value: TAB_VALUES.CHANGE_HISTORY },
    { label: '瑕疵紀錄', value: TAB_VALUES.DEFECT_RECORDS },
];
const TABS_CONTENT_PROPS = { className: 'px-3 pb-3' };

// 功能：API detail -> ShopStockSummaryCard props 格式
const normalizeDetail = (detail) => {
    if (!detail) return null;
    return {
        product: {
            sku: detail.sku ?? '',
            name: detail.productName ?? '',
            spec: detail.variantName ?? '',
            category: detail.categoryName ?? '',
        },
        clubName: detail.clubName ?? '',
        lastInventoryDate: detail.lastInventoryCountDate ?? null,
        defaultPrice: detail.defaultSalePrice ?? null,
        sellableQty: detail.quantity ?? null,
        hasQuantityAdjustmentOrder: detail.hasQuantityAdjustmentOrder === true || detail.isAbnormal === true,
    };
};

// 功能：API 異動統計 -> ShopStockChangeHistory summary 格式
const normalizeChangeSummary = (summary) => {
    if (!summary) return { originalTotal: 0, inbound: 0, outbound: 0, totalInventory: 0, lastInventoryCountDate: null };
    return {
        originalTotal: summary.originalQuantity ?? 0,
        inbound: summary.stockInQuantity ?? 0,
        outbound: summary.stockOutQuantity ?? 0,
        totalInventory: summary.totalQuantity ?? 0,
        lastInventoryCountDate: summary.lastInventoryCountDate ?? null,
    };
};

// 功能：商品庫存明細頁（摘要卡片、異動紀錄 Tab、瑕疵紀錄 Tab）
const ShopStockDetailPage = () => {
    const params = useParams();
    const location = useLocation();
    const history = useHistory();
    const productID = Number(params.productID) || 0;
    const variantID = Number(params.variantID) || 0;
    // clubID 由總覽頁透過網址 query string 帶入；缺漏時無法判斷店鋪，倒回總覽重選
    const clubID = useMemo(() => Number(new URLSearchParams(location.search).get('clubID')) || 0, [location.search]);
    const { activeTab: tabValue, handleTabChange } = useTabState(TAB_VALUES.CHANGE_HISTORY);
    const [recordRange, setRecordRange] = useState(1);
    const [recordType, setRecordType] = useState(INVENTORY_RECORD_TYPE.ALL);

    const { getClubInventoryOptionsApi, postClubInventoryRecordsApi, postClubInventoryDefectiveRecordsApi } = useClubInventoryApi();
    const { options } = useOptions({
        getOptionsApi: getClubInventoryOptionsApi,
        isFetchOnMount: true,
    });

    // 功能：商品摘要（來自 postClubInventoryRecordsApi detail）
    const [detail, setDetail] = useState(null);

    // 功能：異動紀錄 Tab 資料（list + pagination）與摘要
    // 異動區間：0 全部、1 盤點後、2 盤點前（對應 ClubInventoryRecordRangeEnum）
    const [changeHistoryOverviewData, setChangeHistoryOverviewData] = useState({ list: [], pagination: null });
    const [changeSummary, setChangeSummary] = useState(null);
    const [defectOverviewData, setDefectOverviewData] = useState({ list: [], pagination: null });

    // 功能：載入商品摘要（detail）與異動紀錄，recordRange 或換頁時重新載入
    const fetchChangeHistory = useCallback(
        async (pageIndex = 1) => {
            if (!productID || !clubID) return;
            try {
                const resp = await postClubInventoryRecordsApi({ clubID, productID, variantID, pageIndex, pageSize: 50, recordRange, recordType });
                const records = resp?.records ?? {};
                setDetail(normalizeDetail(resp?.detail ?? null));
                setChangeHistoryOverviewData({
                    list: Array.isArray(records.list) ? records.list : [],
                    pagination: {
                        pageIndex,
                        totalPage: records.totalPage ?? 0,
                        totalCount: records.totalCount ?? 0,
                    },
                });
                setChangeSummary(normalizeChangeSummary(resp?.summary ?? null));
            } catch {
                setDetail(null);
                setChangeHistoryOverviewData({ list: [], pagination: null });
                setChangeSummary(normalizeChangeSummary(null));
            }
        },
        [postClubInventoryRecordsApi, clubID, productID, variantID, recordRange, recordType],
    );

    const fetchDefectRecords = useCallback(
        async (pageIndex = 1) => {
            if (!productID || !clubID) return;
            try {
                const resp = await postClubInventoryDefectiveRecordsApi({ clubID, productID, variantID, pageIndex, pageSize: 10 });
                const records = resp?.records ?? {};
                setDefectOverviewData({
                    list: Array.isArray(records.list) ? records.list : [],
                    pagination: {
                        pageIndex,
                        totalPage: records.totalPage ?? 0,
                        totalCount: records.totalCount ?? 0,
                    },
                });
            } catch {
                setDefectOverviewData({ list: [], pagination: null });
            }
        },
        [postClubInventoryDefectiveRecordsApi, clubID, productID, variantID],
    );

    // 缺 productID 或 clubID 時無法查詢店鋪庫存，倒回總覽重選店鋪
    useEffect(() => {
        if (!productID || !clubID) {
            history.replace(CLUB_INVENTORY_SHOP_STOCK_OVERVIEW_ROUTER);
        }
    }, [productID, clubID, history]);

    // 功能：依目前 Tab 載入對應資料；進頁面（預設 Tab）與每次切換 Tab 都重新取得最新資料
    useEffect(() => {
        if (tabValue === TAB_VALUES.CHANGE_HISTORY) {
            fetchChangeHistory();
        } else if (tabValue === TAB_VALUES.DEFECT_RECORDS) {
            fetchDefectRecords();
        }
    }, [tabValue, fetchChangeHistory, fetchDefectRecords]);

    // 功能：開啟可販售數量調整彈窗（目前 HQ 尚未接入 Dialog，先保留 callback 介面）
    const handleEditSellableQty = useCallback(() => {}, []);

    const summary = changeSummary ?? normalizeChangeSummary(null);
    const pageTitle = detail?.clubName ? `${detail.clubName} > 商品庫存明細` : '商品庫存明細';
    const pageActions = [
        {
            label: '返回總覽',
            variant: 'contained',
            as: NavLink,
            to: CLUB_INVENTORY_SHOP_STOCK_OVERVIEW_ROUTER,
        },
    ];

    return (
        <PageLayout className="shop-stock-detail-page">
            <PageLayout.Header title={pageTitle} actions={pageActions} />

            {/* 商品庫存摘要卡片 */}
            <Card className="p-3 mb-3 stock-summary-card">
                <ShopStockSummaryCard
                    product={detail?.product}
                    lastInventoryDate={detail?.lastInventoryDate}
                    defaultPrice={detail?.defaultPrice}
                    sellableQty={detail?.sellableQty}
                    canAdjust={detail?.hasQuantityAdjustmentOrder ?? false}
                    onEditSellableQty={handleEditSellableQty}
                />
            </Card>

            {/* 頁籤：異動紀錄、瑕疵紀錄 */}
            <TabsCardLayout className="shop-stock-detail-tabs" tabs={TABS} activeTab={tabValue} onTabChange={handleTabChange} tabsContent={TABS_CONTENT_PROPS}>
                {tabValue === TAB_VALUES.CHANGE_HISTORY && (
                    <ShopStockChangeHistory
                        className="mt-3"
                        overviewData={changeHistoryOverviewData}
                        updateOverviewPage={fetchChangeHistory}
                        lastInventoryDate={changeSummary?.lastInventoryCountDate}
                        summary={summary}
                        recordRangeOptions={options?.recordRangeOptions}
                        recordRange={recordRange}
                        onRecordRangeChange={setRecordRange}
                        recordType={recordType}
                        onRecordTypeChange={setRecordType}
                    />
                )}

                {tabValue === TAB_VALUES.DEFECT_RECORDS && (
                    <ShopStockDefectRecords className="mt-3" overviewData={defectOverviewData} updateOverviewPage={fetchDefectRecords} />
                )}
            </TabsCardLayout>
        </PageLayout>
    );
};

export default ShopStockDetailPage;
